import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFirestore, collection, query, where, getDocs, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../AuthContext';
import {
  FiBriefcase, FiChevronDown, FiChevronUp, FiEdit, FiTrash2, FiPlus,
  FiCheckCircle, FiAlertTriangle, FiRotateCcw, FiClock
} from 'react-icons/fi';
import './ManageBusinesses.css';

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const isValidTimeFormat = (time) => /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/.test(time);
const isEndAfterStart = (start, end) => {
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  return eh * 60 + em > sh * 60 + sm;
};
const getTimeSlotsByDay = (timeSlots) => {
  const byDay = {};
  DAYS_OF_WEEK.forEach(day => { byDay[day] = timeSlots.filter(s => s.day === day); });
  return byDay;
};

export default function ManageBusinesses() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const db = getFirestore();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [businesses, setBusinesses] = useState([]);
  const [showDeleted, setShowDeleted] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  useEffect(() => {
    if (currentUser) fetchBusinesses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  const fetchBusinesses = async () => {
    setLoading(true);
    setError('');
    try {
      const q = query(collection(db, 'businesses'), where('ownerId', '==', currentUser.uid));
      const snapshot = await getDocs(q);
      const list = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setBusinesses(list);
    } catch (err) {
      console.error('Error fetching businesses:', err);
      setError(`Couldn't load your businesses: ${err.code || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const visibleBusinesses = businesses.filter(b => (showDeleted ? true : !b.isDeleted));

  const toggleExpand = (id) => setExpandedId(prev => (prev === id ? null : id));

  const softDeleteBusiness = async (businessId) => {
    try {
      await updateDoc(doc(db, 'businesses', businessId), {
        isDeleted: true,
        deletedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      setBusinesses(prev => prev.map(b => (b.id === businessId ? { ...b, isDeleted: true } : b)));
      setConfirmDeleteId(null);
      if (expandedId === businessId) setExpandedId(null);
    } catch (err) {
      console.error('Error deleting business:', err);
      setError(`Couldn't delete business: ${err.code || err.message}`);
    }
  };

  const restoreBusiness = async (businessId) => {
    try {
      await updateDoc(doc(db, 'businesses', businessId), {
        isDeleted: false,
        deletedAt: null,
        updatedAt: serverTimestamp(),
      });
      setBusinesses(prev => prev.map(b => (b.id === businessId ? { ...b, isDeleted: false } : b)));
    } catch (err) {
      console.error('Error restoring business:', err);
      setError(`Couldn't restore business: ${err.code || err.message}`);
    }
  };

  const updateServicesForBusiness = async (businessId, newServices) => {
    try {
      await updateDoc(doc(db, 'businesses', businessId), {
        services: newServices,
        updatedAt: serverTimestamp(),
      });
      setBusinesses(prev => prev.map(b => (b.id === businessId ? { ...b, services: newServices } : b)));
    } catch (err) {
      console.error('Error updating services:', err);
      setError(`Couldn't save service changes: ${err.code || err.message}`);
    }
  };

  if (loading) return <div className="manage-businesses-page">Loading...</div>;

  return (
    <div className="manage-businesses-page">
      <div className="manage-businesses-container">
        <div className="manage-header">
          <h1><FiBriefcase /> Manage Businesses</h1>
          <div className="manage-header-actions">
            <label className="show-deleted-toggle">
              <input type="checkbox" checked={showDeleted} onChange={e => setShowDeleted(e.target.checked)} />
              Show deleted
            </label>
            <button className="btn-primary" onClick={() => navigate('/create-business')}>
              <FiPlus /> New Business
            </button>
          </div>
        </div>

        {error && <div className="form-error"><FiAlertTriangle /> {error}</div>}

        {visibleBusinesses.length === 0 && (
          <div className="empty-state">
            <FiBriefcase size={40} />
            <p>{showDeleted ? 'No businesses at all yet.' : 'No active businesses. Create one to get started.'}</p>
          </div>
        )}

        <div className="business-cards">
          {visibleBusinesses.map(business => (
            <div key={business.id} className={`business-manage-card ${business.isDeleted ? 'deleted' : ''}`}>
              <div className="business-manage-header" onClick={() => toggleExpand(business.id)}>
                <div className="business-manage-identity">
                  {business.profileImageUrl ? (
                    <img src={business.profileImageUrl} alt={business.businessName} className="business-thumb" />
                  ) : (
                    <div className="business-thumb-placeholder"><FiBriefcase /></div>
                  )}
                  <div>
                    <h3>{business.businessName}</h3>
                    <span className="business-manage-meta">
                      {business.category} · {(business.services || []).length} service{(business.services || []).length === 1 ? '' : 's'}
                    </span>
                  </div>
                </div>

                <div className="business-manage-badges">
                  {business.isDeleted && <span className="badge-deleted">Deleted</span>}
                  <span className={`status-badge ${business.status}`}>
                    {business.status === 'active' ? <FiCheckCircle /> : <FiAlertTriangle />}
                    {business.status}
                  </span>
                  {expandedId === business.id ? <FiChevronUp /> : <FiChevronDown />}
                </div>
              </div>

              <div className="business-manage-toolbar">
                <button className="icon-text-btn" onClick={() => navigate(`/edit-business/${business.id}`)}>
                  <FiEdit /> Edit Details
                </button>
                {business.isDeleted ? (
                  <button className="icon-text-btn" onClick={() => restoreBusiness(business.id)}>
                    <FiRotateCcw /> Restore
                  </button>
                ) : confirmDeleteId === business.id ? (
                  <span className="confirm-delete-row">
                    <span>Delete this business?</span>
                    <button className="icon-text-btn danger" onClick={() => softDeleteBusiness(business.id)}>Yes, delete</button>
                    <button className="icon-text-btn" onClick={() => setConfirmDeleteId(null)}>Cancel</button>
                  </span>
                ) : (
                  <button className="icon-text-btn danger" onClick={() => setConfirmDeleteId(business.id)}>
                    <FiTrash2 /> Delete
                  </button>
                )}
              </div>

              {expandedId === business.id && (
                <ServicesManager
                  business={business}
                  onSaveServices={(newServices) => updateServicesForBusiness(business.id, newServices)}
                />
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ServicesManager({ business, onSaveServices }) {
  const [services, setServices] = useState(business.services || []);
  const [editingIndex, setEditingIndex] = useState(null); // -1 = adding new
  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  const [duration, setDuration] = useState('');
  const [description, setDescription] = useState('');
  const [timeSlots, setTimeSlots] = useState([]);
  const [selectedDay, setSelectedDay] = useState('Monday');
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('17:00');
  const [editingSlotIndex, setEditingSlotIndex] = useState(null);
  const [formError, setFormError] = useState('');

  const resetSlotForm = () => {
    setSelectedDay('Monday'); setStartTime('09:00'); setEndTime('17:00'); setEditingSlotIndex(null);
  };

  const resetServiceForm = () => {
    setName(''); setPrice(''); setDuration(''); setDescription('');
    setTimeSlots([]); setEditingIndex(null); resetSlotForm(); setFormError('');
  };

  const startAdd = () => { resetServiceForm(); setEditingIndex(-1); };

  const startEdit = (index) => {
    if (editingIndex === index) { resetServiceForm(); return; }
    const s = services[index];
    setName(s.name); setPrice(String(s.price)); setDuration(String(s.duration));
    setDescription(s.description || ''); setTimeSlots(s.timeSlots || []);
    setEditingIndex(index); resetSlotForm();
  };

  const addSlot = () => {
    if (!isValidTimeFormat(startTime) || !isValidTimeFormat(endTime)) {
      setFormError('Use HH:MM format for times.');
      return;
    }
    if (!isEndAfterStart(startTime, endTime)) {
      setFormError('End time must be after start time.');
      return;
    }
    const slotData = {
      id: editingSlotIndex !== null ? timeSlots[editingSlotIndex].id : Date.now().toString(),
      day: selectedDay, startTime, endTime,
    };
    let updated;
    if (editingSlotIndex !== null) {
      updated = [...timeSlots];
      updated[editingSlotIndex] = slotData;
    } else {
      const overlap = timeSlots.find(s =>
        s.day === selectedDay &&
        ((startTime >= s.startTime && startTime < s.endTime) ||
         (endTime > s.startTime && endTime <= s.endTime) ||
         (startTime <= s.startTime && endTime >= s.endTime))
      );
      if (overlap) {
        setFormError('This time slot overlaps with an existing one on the same day.');
        return;
      }
      updated = [...timeSlots, slotData];
    }
    updated.sort((a, b) => {
      const dayDiff = DAYS_OF_WEEK.indexOf(a.day) - DAYS_OF_WEEK.indexOf(b.day);
      return dayDiff !== 0 ? dayDiff : a.startTime.localeCompare(b.startTime);
    });
    setTimeSlots(updated);
    setFormError('');
    resetSlotForm();
  };

  const deleteSlot = (index) => setTimeSlots(prev => prev.filter((_, i) => i !== index));

  const saveService = () => {
    if (!name || !price || !duration) {
      setFormError('Please fill in name, price, and duration.');
      return;
    }
    if (timeSlots.length === 0) {
      setFormError('Add at least one time slot.');
      return;
    }
    const isEditingExisting = editingIndex !== null && editingIndex >= 0;
    const serviceData = {
      id: isEditingExisting ? services[editingIndex].id : Date.now().toString(),
      name, price: parseFloat(price), duration: parseInt(duration, 10), description, timeSlots,
    };
    const updated = isEditingExisting
      ? services.map((s, i) => (i === editingIndex ? serviceData : s))
      : [...services, serviceData];
    setServices(updated);
    onSaveServices(updated);
    resetServiceForm();
  };

  const deleteService = (index) => {
    const updated = services.filter((_, i) => i !== index);
    setServices(updated);
    onSaveServices(updated);
    if (editingIndex === index) resetServiceForm();
  };

  return (
    <div className="services-manager">
      {formError && <div className="form-error small"><FiAlertTriangle /> {formError}</div>}

      {services.length === 0 && editingIndex === null && (
        <p className="no-services-text">No services yet for this business.</p>
      )}

      {services.map((service, index) => (
        <div key={service.id} className="service-manage-row">
          <div className="service-manage-summary">
            <div>
              <strong>{service.name}</strong>
              <span className="service-meta"> · R{service.price} · {service.duration} mins</span>
            </div>
            <div className="service-manage-actions">
              <button className="icon-btn" onClick={() => startEdit(index)}><FiEdit /></button>
              <button className="icon-btn danger" onClick={() => deleteService(index)}><FiTrash2 /></button>
            </div>
          </div>

          {service.timeSlots?.length > 0 && (
            <div className="service-slots-preview">
              {Object.entries(getTimeSlotsByDay(service.timeSlots)).map(([day, slots]) =>
                slots.length > 0 && (
                  <span key={day} className="slot-chip">
                    <FiClock /> {day.slice(0, 3)}: {slots.map(s => `${s.startTime}-${s.endTime}`).join(', ')}
                  </span>
                )
              )}
            </div>
          )}

          {editingIndex === index && (
            <ServiceEditForm
              {...{ name, setName, price, setPrice, duration, setDuration, description, setDescription,
                timeSlots, selectedDay, setSelectedDay, startTime, setStartTime, endTime, setEndTime,
                editingSlotIndex, addSlot, editSlot: (i) => {
                  const slot = timeSlots[i];
                  setSelectedDay(slot.day); setStartTime(slot.startTime); setEndTime(slot.endTime); setEditingSlotIndex(i);
                }, deleteSlot, resetSlotForm, saveService, resetServiceForm, isNew: false }}
            />
          )}
        </div>
      ))}

      {editingIndex === -1 && (
        <div className="service-manage-row new-service-row">
          <ServiceEditForm
            {...{ name, setName, price, setPrice, duration, setDuration, description, setDescription,
              timeSlots, selectedDay, setSelectedDay, startTime, setStartTime, endTime, setEndTime,
              editingSlotIndex, addSlot, editSlot: (i) => {
                const slot = timeSlots[i];
                setSelectedDay(slot.day); setStartTime(slot.startTime); setEndTime(slot.endTime); setEditingSlotIndex(i);
              }, deleteSlot, resetSlotForm, saveService, resetServiceForm, isNew: true }}
          />
        </div>
      )}

      {editingIndex === null && (
        <button className="btn-secondary small" onClick={startAdd}>
          <FiPlus /> Add Service
        </button>
      )}
    </div>
  );
}

function ServiceEditForm({
  name, setName, price, setPrice, duration, setDuration, description, setDescription,
  timeSlots, selectedDay, setSelectedDay, startTime, setStartTime, endTime, setEndTime,
  editingSlotIndex, addSlot, editSlot, deleteSlot, resetSlotForm, saveService, resetServiceForm, isNew,
}) {
  return (
    <div className="service-edit-form">
      <label>Service Name *</label>
      <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Haircut" />

      <label>Price (R) *</label>
      <input type="number" value={price} onChange={e => setPrice(e.target.value)} />

      <label>Duration (minutes) *</label>
      <input type="number" value={duration} onChange={e => setDuration(e.target.value)} />

      <label>Description</label>
      <textarea rows={2} value={description} onChange={e => setDescription(e.target.value)} />

      <div className="time-slot-section">
        <label>Availability *</label>

        {timeSlots.length > 0 && (
          <div className="time-slot-list">
            {timeSlots.map((slot, i) => (
              <div key={slot.id} className="time-slot-item">
                <span>{slot.day.slice(0, 3)}: {slot.startTime}–{slot.endTime}</span>
                <div>
                  <button className="icon-btn" onClick={() => editSlot(i)}><FiEdit /></button>
                  <button className="icon-btn danger" onClick={() => deleteSlot(i)}><FiTrash2 /></button>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="day-selector">
          {DAYS_OF_WEEK.map(day => (
            <button
              key={day}
              type="button"
              className={`day-chip ${selectedDay === day ? 'selected' : ''}`}
              onClick={() => setSelectedDay(day)}
            >
              {day.slice(0, 3)}
            </button>
          ))}
        </div>

        <div className="time-input-row">
          <div>
            <label>Start</label>
            <input type="time" value={startTime} onChange={e => setStartTime(e.target.value)} />
          </div>
          <div>
            <label>End</label>
            <input type="time" value={endTime} onChange={e => setEndTime(e.target.value)} />
          </div>
        </div>

        <button className="btn-secondary small" onClick={addSlot}>
          {editingSlotIndex !== null ? 'Update Time Slot' : 'Add Time Slot'}
        </button>
        {editingSlotIndex !== null && (
          <button className="btn-secondary small" onClick={resetSlotForm}>Cancel</button>
        )}
      </div>

      <div className="service-form-actions">
        <button className="btn-primary small" onClick={saveService}>
          {isNew ? 'Add Service' : 'Update Service'}
        </button>
        <button className="btn-secondary small" onClick={resetServiceForm}>Cancel</button>
      </div>
    </div>
  );
}